import { useState } from 'react';
import type { EquipePlacar } from '../types';

interface Props {
  equipe: EquipePlacar;
  selecionado: boolean;
  onSelect: () => void;
  onSalvarVagas: (vagas: number) => Promise<boolean>;
}

export default function CartaoEquipe({ equipe, selecionado, onSelect, onSalvarVagas }: Props) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState('');
  const [salvando, setSalvando] = useState(false);

  const alocados = Number(equipe.alocados) || 0;
  const vagas = equipe.vagas_alvo;
  const faltam = Number(equipe.faltam) || 0;
  const mulheres = Number(equipe.mulheres) || 0;
  const homens = Number(equipe.homens) || 0;
  const primeiro = Number(equipe.no_primeiro_servir) || 0;

  const textoFuncoes = (equipe.funcoes ?? '').trim();
  const temFuncoes = textoFuncoes.length > 0;
  const resumoFuncoes =
    textoFuncoes.length > 220 ? `${textoFuncoes.slice(0, 220).trim()}…` : textoFuncoes;

  const pct = vagas !== null && vagas > 0 ? Math.min(100, (alocados / vagas) * 100) : 0;
  const corBarra =
    vagas !== null && alocados === vagas
      ? 'bg-green-500'
      : vagas !== null && alocados > vagas
        ? 'bg-red-500'
        : 'bg-amber-500';

  function iniciarEdicao() {
    setValor(vagas !== null ? String(vagas) : '');
    setEditando(true);
  }

  async function confirmar() {
    const v = valor.trim();
    if (!v) return;
    const num = Number(v);
    if (!Number.isInteger(num) || num < 0) return;
    setSalvando(true);
    const ok = await onSalvarVagas(num);
    setSalvando(false);
    if (ok) setEditando(false);
  }

  return (
    <div
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className={`cursor-pointer rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)] transition-colors ${
        selecionado
          ? 'ring-2 ring-primary-500'
          : 'hover:bg-primary-50'
      }`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm font-semibold uppercase text-foreground-950">
            {equipe.equipe}
          </span>
          {temFuncoes && (
            <span
              title={resumoFuncoes}
              className="flex h-4 w-4 shrink-0 cursor-help items-center justify-center text-secondary-400"
            >
              <i className="ri-file-text-line text-sm" />
            </span>
          )}
        </span>

        {editando ? (
          <input
            autoFocus
            type="number"
            min={0}
            step={1}
            value={valor}
            disabled={salvando}
            onChange={(e) => setValor(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirmar();
              if (e.key === 'Escape') setEditando(false);
            }}
            onBlur={() => setEditando(false)}
            onClick={(e) => e.stopPropagation()}
            className="w-16 rounded border border-primary-400 px-2 py-0.5 text-right text-sm text-foreground-950 outline-none"
          />
        ) : vagas !== null ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              iniciarEdicao();
            }}
            title="Editar vagas"
            className="cursor-pointer whitespace-nowrap rounded px-1 text-sm font-medium text-foreground-700 transition-colors hover:bg-secondary-100"
          >
            {alocados}/{vagas}
          </button>
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              iniciarEdicao();
            }}
            className="cursor-pointer whitespace-nowrap rounded px-1 text-sm font-medium text-foreground-700 transition-colors hover:bg-secondary-100"
          >
            {alocados} <span className="text-primary-500">definir</span>
          </button>
        )}
      </div>

      {vagas !== null && (
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-secondary-100">
          <div className={`h-2 rounded-full ${corBarra}`} style={{ width: `${pct}%` }} />
        </div>
      )}

      <p className="mt-2 text-xs text-secondary-500">
        {mulheres} M · {homens} H · {primeiro} no 1º servir
      </p>

      {vagas !== null && faltam > 0 && (
        <p className="mt-1 text-xs font-medium text-amber-600">faltam {faltam}</p>
      )}
    </div>
  );
}